import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import CollateralCard, { getAvgReadPct } from './CollateralCard';
import PlaybooksPanel from './PlaybooksPanel';
import type { CollateralData } from '../../api/client';

type LibTab = 'collaterals' | 'playbooks';

export default function LibraryScreen() {
  const { colls, playbooks, toast, libraryTab: tab, setLibraryTab: setTab } = useApp();
  const { isMobile, rp } = useBreakpoint();
  const [sel, setSel] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [senderFilter, setSenderFilter] = useState('');
  const [previewModal, setPreviewModal] = useState<{ coll: CollateralData; page: number } | null>(null);
  const px = rp({ mobile: '14px', tablet: '18px', desktop: '24px' });

  const filtered = useMemo(() => {
    let items = [...colls];
    if (typeFilter !== 'all') items = items.filter(c => c.type === typeFilter);
    if (senderFilter) items = items.filter(c => c.sharedBy === senderFilter);
    if (search) items = items.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.tags.some(t => t.toLowerCase().includes(search.toLowerCase())));
    if (readFilter) {
      items = items.filter(c => {
        const avg = getAvgReadPct(c);
        if (readFilter === 'unread') return avg === 0;
        if (readFilter === 'partial') return avg > 0 && avg < 100;
        if (readFilter === 'read') return avg === 100;
        return true;
      });
    }
    if (sortBy === 'name') items.sort((a, b) => a.name.localeCompare(b.name));
    else if (sortBy === 'read-desc') items.sort((a, b) => getAvgReadPct(b) - getAvgReadPct(a));
    return items;
  }, [colls, typeFilter, senderFilter, search, readFilter, sortBy]);

  const toggleCard = (id: number | string) => {
    const sid = String(id);
    setSel(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);
  };

  const selectStyle: React.CSSProperties = {
    padding: '7px 24px 7px 9px', borderRadius: '8px', border: '1.5px solid var(--border2)',
    fontFamily: 'inherit', fontSize: '12px', background: 'white', cursor: 'pointer',
    outline: 'none', appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23718096' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 7px center',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: `14px ${px} 0`, display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: rp({ mobile: '16px', desktop: '18px' }), fontWeight: '700', color: 'var(--navy)' }}>Collateral Library</div>
          {!isMobile && <div style={{ fontSize: '12px', color: 'var(--text3)' }}>Select collaterals · create a Sales Playbook</div>}
        </div>
        <button onClick={() => toast('Upload coming soon!')} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '7px 12px', borderRadius: '8px', border: '1.5px solid var(--border2)', background: 'white', color: 'var(--text2)', fontFamily: 'inherit', fontSize: '12px', fontWeight: '500', cursor: 'pointer', flexShrink: 0 }}>
          <i className="ti ti-upload" />{!isMobile && ' Upload'}
        </button>
      </div>

      {/* Selection bar */}
      {sel.length > 0 && (
        <div style={{ background: 'var(--navy)', color: 'white', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: `6px ${px} 0` , borderRadius: '10px' }}>
          <div style={{ display: 'flex', gap: '5px', flex: 1, flexWrap: 'wrap' }}>
            {sel.map(id => { const c = colls.find(x => (x._id || String(x.id)) === id); if (!c) return null; return (
              <div key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(255,255,255,.12)', padding: '2px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '500', maxWidth: '140px' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name.substring(0, 18)}{c.name.length > 18 ? '…' : ''}</span>
                <button onClick={() => toggleCard(id)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.6)', cursor: 'pointer', fontSize: '13px', lineHeight: 1, padding: '0 0 0 2px' }}>×</button>
              </div>
            ); })}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            <button onClick={() => setSel([])} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '7px', background: 'rgba(255,255,255,.1)', border: '1px solid rgba(255,255,255,.2)', color: 'white', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer' }}>
              <i className="ti ti-x" /> Clear
            </button>
            {!isMobile && <button onClick={() => toast('Quick Share sent!')} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '7px', background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.2)', color: 'white', fontFamily: 'inherit', fontSize: '11px', cursor: 'pointer' }}>
              <i className="ti ti-bolt" style={{ color: '#FCD34D' }} /> Quick Share
            </button>}
            <button onClick={() => toast('Opening playbook wizard…')} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 12px', borderRadius: '7px', background: '#C8973A', border: 'none', color: 'white', fontFamily: 'inherit', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>
              <i className="ti ti-book-2" /> {isMobile ? 'Build' : 'Build Playbook'}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', margin: `8px ${px} 0`, flexShrink: 0 }}>
        {(['collaterals', 'playbooks'] as LibTab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '9px 14px', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: tab === t ? 'var(--navy)' : 'var(--text3)', borderBottom: tab === t ? '2px solid var(--gold)' : '2px solid transparent', display: 'flex', alignItems: 'center', gap: '5px', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
            <i className={`ti ${t === 'collaterals' ? 'ti-files' : 'ti-book-2'}`} />
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'playbooks' && <span style={{ background: '#EDF2F7', color: '#4A5568', fontSize: '10px', fontWeight: '700', borderRadius: '20px', padding: '1px 7px' }}>{playbooks.length}</span>}
          </button>
        ))}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: `12px ${px}` }}>
        {tab === 'collaterals' && (
          <>
            {/* Filters */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              <div style={{ display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: '160px' }}>
                  <i className="ti ti-search" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--text3)', pointerEvents: 'none' }} />
                  <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search collaterals…" style={{ width: '100%', padding: '7px 10px 7px 28px', borderRadius: '8px', border: '1.5px solid var(--border2)', fontFamily: 'inherit', fontSize: '12px', outline: 'none', background: 'white', boxSizing: 'border-box' }} />
                </div>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ ...selectStyle }}>
                  <option value="default">Default</option>
                  <option value="name">Name A–Z</option>
                  <option value="read-desc">Most read</option>
                </select>
                {!isMobile && (
                  <select value={senderFilter} onChange={e => setSenderFilter(e.target.value)} style={{ ...selectStyle }}>
                    <option value="">All senders</option>
                    <option value="Rahul Nair">Rahul Nair</option>
                    <option value="Sneha Kapoor">Sneha Kapoor</option>
                    <option value="Arjun Mehta">Arjun Mehta</option>
                  </select>
                )}
              </div>
              <div style={{ display: 'flex', gap: '7px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ ...selectStyle }}>
                  <option value="all">Type: All</option>
                  <option value="pdf">PDF</option>
                  <option value="ppt">Presentations</option>
                  <option value="video">Videos</option>
                  <option value="url">URLs</option>
                </select>
                <select value={readFilter} onChange={e => setReadFilter(e.target.value)} style={{ ...selectStyle }}>
                  <option value="">Read: All</option>
                  <option value="unread">Unread</option>
                  <option value="partial">Partial</option>
                  <option value="read">Fully read</option>
                </select>
                <span style={{ fontSize: '11px', color: 'var(--text3)' }}>{filtered.length} item{filtered.length !== 1 ? 's' : ''}</span>
                {(search || typeFilter !== 'all' || readFilter || senderFilter) && (
                  <button onClick={() => { setSearch(''); setTypeFilter('all'); setReadFilter(''); setSenderFilter(''); }} style={{ fontSize: '11px', color: 'var(--text3)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: rp({ mobile: 'repeat(2,1fr)', tablet: 'repeat(3,1fr)', desktop: 'repeat(auto-fill,minmax(160px,1fr))' }), gap: '12px' }}>
              {filtered.map(coll => (
                <CollateralCard key={coll._id || coll.id} coll={coll}
                  selected={sel.includes(coll._id || String(coll.id))}
                  onToggle={toggleCard}
                  onPreview={() => setPreviewModal({ coll, page: 0 })}
                  onAnalytics={() => toast(`Analytics for ${coll.name}`)}
                />
              ))}
            </div>
          </>
        )}
        {tab === 'playbooks' && <PlaybooksPanel />}
      </div>

      {/* Preview modal */}
      {previewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,27,45,.52)', zIndex: 500, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? '0' : '16px' }} onClick={() => setPreviewModal(null)}>
          <div style={{ background: 'white', borderRadius: isMobile ? '20px 20px 0 0' : '16px', width: '100%', maxWidth: isMobile ? '100%' : '560px', maxHeight: isMobile ? '90vh' : '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }} onClick={e => e.stopPropagation()}>
            {isMobile && <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: '#CBD5E0', margin: '12px auto 0' }} />}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{previewModal.coll.name}</div>
                <div style={{ fontSize: '11px', color: 'var(--text3)' }}>{previewModal.coll.size} · {previewModal.coll.pages} page{previewModal.coll.pages !== 1 ? 's' : ''}</div>
              </div>
              <button onClick={() => setPreviewModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: '20px', padding: '2px' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
              {(previewModal.coll.pdata || []).map((p, i) => (
                <div key={i} style={{ marginBottom: '12px', padding: '14px', background: i === previewModal.page ? '#FDF4E7' : 'var(--surface)', borderRadius: '10px', border: i === previewModal.page ? '2px solid #C8973A' : '1.5px solid transparent', cursor: 'pointer' }} onClick={() => setPreviewModal(prev => prev ? { ...prev, page: i } : null)}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#C8973A', marginBottom: '3px', textTransform: 'uppercase' }}>Page {i+1}</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)', marginBottom: '2px' }}>{p.t}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text3)', marginBottom: '5px' }}>{p.s}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text2)' }}>{p.b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
